import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningIcon from '@mui/icons-material/Warning';
import { IconButton, Snackbar } from '@mui/material';
import type { User } from '@takeshape/schema';
import type React from 'react';
import ReactMarkdown from 'react-markdown';
import styled from 'styled-components';
import type { MessageType } from '../../types.js';
import UserLabel from '../user-label/index.js';

const Message = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  white-space: pre-wrap;

  code {
    color: white;
    font0size: 0.7rem;
  }
`;

export type MessagesProps = {
  readonly active: boolean;
  readonly message: string;
  readonly user?: User;
  readonly type: MessageType;
  readonly dismissMessage: () => void;
};

const MessageContents = styled.div`
  display: flex;
`;

const MessageMarkdown = styled(ReactMarkdown)`
  max-width: 1000px;
  margin-left: 1rem;
`;

const StyledErrorIcon = styled(ErrorOutlineIcon)`
  color: ${({ theme }): string => theme.palette.error.main};
`;

const StyledWarningIcon = styled(WarningIcon)`
  color: ${({ theme }): string => theme.palette.warning.main};
`;

const StyledCheckIcon = styled(CheckCircleOutlineIcon)`
  color: ${({ theme }): string => theme.palette.success.main};
`;

export const Messages: React.FC<MessagesProps> = (props: MessagesProps) => {
  const { user, active, message, type, dismissMessage } = props;

  let Icon: typeof StyledErrorIcon | typeof StyledWarningIcon | typeof StyledCheckIcon;
  if (type === 'error') {
    Icon = StyledErrorIcon;
  } else if (type === 'warning') {
    Icon = StyledWarningIcon;
  } else {
    Icon = StyledCheckIcon;
  }

  return (
    <Snackbar
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center'
      }}
      open={active}
      onClose={dismissMessage}
      message={
        <MessageContents data-testid="message-contents">
          {user ? <UserLabel user={user} /> : <Icon />}
          <MessageMarkdown escapeHtml source={message} />
        </MessageContents>
      }
      action={
        <Message>
          <IconButton size="small" aria-label="close" color="inherit" onClick={dismissMessage}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Message>
      }
    />
  );
};
